import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useTheme } from '../../theme/useTheme';
import { useGeniePanelScrollFabSync } from '../../context/GeniePanelContext';
import { genieClient, ChatMessage, ChatResponse, ChatContext, AutocompleteResult } from '../../services/genieClient';
import { ArrowsPointingInIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import { GenieLogo } from './GenieLogo';
import { 
  exportChatAsPdf, 
  exportChatAsHtml, 
  exportTablesAsCsv,
  exportMessageAsPdf,
  exportMessageAsHtml,
  exportMessageTablesAsCsv,
  isWelcomeMessage
} from '../../utils/exportUtils';

// Debounce hook for autocomplete
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

interface GenieProps {
  context?: ChatContext;
  onAction?: (action: { type: string; label: string; path?: string; value?: string }) => void;
  onNavigateToGene?: (gene: string) => void;
}

// Conversation state for multi-turn context
interface ConversationState {
  lastGenes: string[];
  lastIntent: string | null;
  lastTopic: string | null;
  lastTherapeuticArea: string | null;
}

// Known gene patterns for detection
const GENE_PATTERN = /\b([A-Z][A-Z0-9]{1,10})\b/g;
const COMMON_WORDS = new Set([
  'THE', 'AND', 'FOR', 'ARE', 'BUT', 'NOT', 'YOU', 'ALL', 'CAN', 'HER', 'WAS', 'ONE',
  'OUR', 'OUT', 'HAS', 'HIS', 'HOW', 'ITS', 'LET', 'MAY', 'SAY', 'SHE', 'TOO', 'USE',
  'WAY', 'WHO', 'BOY', 'DID', 'MAN', 'TOP', 'NEW', 'NOW', 'OLD', 'SEE', 'WAY', 'DAY',
  'GET', 'HAS', 'HIM', 'HOW', 'MAN', 'NEW', 'NOW', 'OLD', 'SEE', 'TWO', 'GO', 'NO',
  'GENE', 'GENES', 'DISEASE', 'DISEASES', 'SHOW', 'TELL', 'GIVE', 'FIND', 'HELP',
  'MORE', 'WHAT', 'WHEN', 'WITH', 'THAT', 'THIS', 'FROM', 'HAVE', 'BEEN', 'ALSO',
  'ABOUT', 'INTO', 'OVER', 'SUCH', 'ONLY', 'OTHER', 'THAN', 'THEN', 'THESE', 'SOME',
  'COULD', 'WOULD', 'THEIR', 'WHICH', 'THERE', 'WHERE', 'AFTER', 'BEFORE', 'BOTH',
  'DCM', 'HCM', 'HF', 'ARVC', 'CKD', 'ADPKD', // Disease abbreviations
  'DNA', 'RNA', 'FDR', 'DEG', 'TPM', 'LOG', 'FC', // Common scientific abbreviations
  'YES', 'NO', 'OK', 'TOP', 'LOW', 'HIGH', 'MAX', 'MIN'
]);

// List of validated genes (subset for quick matching)
const KNOWN_GENES = new Set([
  'LMNA', 'TTN', 'MYH7', 'MYH6', 'MYBPC3', 'SCN5A', 'PKD1', 'PKD2', 'KCNQ1', 'KCNH2',
  'RYR2', 'DSP', 'DSC2', 'DSG2', 'PKP2', 'JUP', 'TMEM43', 'PLN', 'CASQ2', 'CALM1',
  'STAT4', 'NPPA', 'NPPB', 'FOS', 'JUN', 'MYC', 'TP53', 'EGFR', 'VEGFA', 'ACE',
  'ACE2', 'AGT', 'AGTR1', 'ADRB1', 'ADRB2', 'NOS3', 'EDN1', 'EDNRA', 'EDNRB',
  'CLU', 'ALPK3', 'BAG3', 'FLNC', 'DES', 'LAMP2', 'PRKAG2', 'GLA', 'FHL1',
  'TAZ', 'EMD', 'LMNA', 'TNNT2', 'TNNI3', 'TPM1', 'ACTC1', 'MYL2', 'MYL3'
]);

export const Genie: React.FC<GenieProps> = ({ context, onAction: _onAction, onNavigateToGene }) => {
  const { mode } = useTheme();
  const setHideScrollFab = useGeniePanelScrollFabSync();
  const [isOpen, setIsOpen] = useState(false);
  /** Expands chat to full viewport (above page chrome). Reset when panel closes. */
  const [isMaximized, setIsMaximized] = useState(false);
  const [showGinie, setShowGenie] = useState(true);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([
    "Tell me about LMNA gene",
    "What diseases are related to heart failure?",
    "Compare LMNA vs TTN",
  ]);
  const [error, setError] = useState<string | null>(null);
  
  // Autocomplete state
  const [autocompleteResults, setAutocompleteResults] = useState<AutocompleteResult[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedAutocompleteIndex, setSelectedAutocompleteIndex] = useState(-1);
  
  // Export state
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  
  // Multi-turn conversation state
  const [conversationState, setConversationState] = useState<ConversationState>({
    lastGenes: [],
    lastIntent: null,
    lastTopic: null,
    lastTherapeuticArea: null
  });
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  
  // Debounce the input value for autocomplete
  const debouncedInput = useDebounce(inputValue, 200);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Hide global scroll-to-top while panel is open or until width close animation finishes
  useLayoutEffect(() => {
    if (isOpen) setHideScrollFab(true);
  }, [isOpen, setHideScrollFab]);

  useEffect(() => {
    if (!isOpen) setIsMaximized(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !isMaximized) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMaximized(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, isMaximized]);

  // Fetch autocomplete suggestions when input changes
  useEffect(() => {
    const fetchAutocomplete = async () => {
      // Only trigger autocomplete if input looks like a gene query
      // Look for gene-like patterns at the end of the input
      const words = debouncedInput.trim().split(/\s+/);
      const lastWord = words[words.length - 1]?.toUpperCase() || '';
      
      // Check if last word looks like a partial gene symbol
      if (lastWord.length >= 2 && /^[A-Z][A-Z0-9]*$/.test(lastWord)) {
        try {
          const response = await genieClient.autocomplete(lastWord, 6);
          if (response.results.length > 0) {
            setAutocompleteResults(response.results);
            setShowAutocomplete(true);
            setSelectedAutocompleteIndex(-1);
          } else {
            setShowAutocomplete(false);
          }
        } catch (err) {
          console.error('Autocomplete error:', err);
          setShowAutocomplete(false);
        }
      } else {
        setShowAutocomplete(false);
      }
    };

    if (debouncedInput.length >= 2) {
      fetchAutocomplete();
    } else {
      setShowAutocomplete(false);
    }
  }, [debouncedInput]);

  // Close autocomplete when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target as Node) &&
          inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowAutocomplete(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is inside the export button OR inside the export dropdown menu
      const exportButton = document.querySelector('[aria-label="Export"]');
      const exportMenu = exportButton?.parentElement;
      if (showExportMenu && exportMenu && !exportMenu.contains(target)) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  // Welcome message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `👋 Hello! I'm **Genie**, your AI assistant for TargetX.

I can help you:
- Learn about genes and their disease associations
- Compare multiple genes side by side
- Explore perturbation analysis and mechanisms
- Navigate the platform

**Try clicking on any gene symbol** in my responses to learn more about it!

What would you like to explore today?`,
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isOpen, messages.length]);

  // Extract genes from text
  const extractGenesFromText = useCallback((text: string): string[] => {
    const matches = text.toUpperCase().match(GENE_PATTERN) || [];
    return matches.filter(match => 
      KNOWN_GENES.has(match) || 
      (!COMMON_WORDS.has(match) && match.length >= 3 && match.length <= 8)
    );
  }, []);

  // Resolve pronouns and context references
  const resolveContextReferences = useCallback((text: string): string => {
    const lowerText = text.toLowerCase();
    
    // Handle pronouns referring to last gene(s)
    if (conversationState.lastGenes.length > 0) {
      const lastGene = conversationState.lastGenes[0];
      
      // "it", "this gene", "that gene" -> last gene
      if (/\b(it|this gene|that gene|the gene)\b/.test(lowerText) && !extractGenesFromText(text).length) {
        return text.replace(/\b(it|this gene|that gene|the gene)\b/gi, lastGene);
      }
      
      // "them", "these genes", "those genes" -> last genes
      if (/\b(them|these genes|those genes|the genes)\b/.test(lowerText) && conversationState.lastGenes.length > 1) {
        return text.replace(/\b(them|these genes|those genes|the genes)\b/gi, 
          conversationState.lastGenes.slice(0, 3).join(' and '));
      }
      
      // "same for X" or "what about X" - apply last intent to new gene
      if (/\b(same for|what about|how about|and for)\b/.test(lowerText)) {
        const newGenes = extractGenesFromText(text);
        if (newGenes.length > 0 && conversationState.lastIntent) {
          // Keep the message but we'll add context
        }
      }
      
      // "tell me more", "more details", "expand on that"
      if (/\b(tell me more|more details|expand|elaborate|continue)\b/.test(lowerText)) {
        return `Tell me more about ${lastGene}`;
      }
    }
    
    return text;
  }, [conversationState, extractGenesFromText]);

  // Update conversation state based on response
  const updateConversationState = useCallback((userMessage: string, response: ChatResponse) => {
    const genes = extractGenesFromText(userMessage + ' ' + response.message);
    const uniqueGenes = [...new Set(genes)];
    
    // Detect intent from message
    let intent = 'general';
    const lowerMsg = userMessage.toLowerCase();
    
    if (lowerMsg.includes('disease') || lowerMsg.includes('associated')) intent = 'disease';
    else if (lowerMsg.includes('phenotype') || lowerMsg.includes('symptom')) intent = 'phenotype';
    else if (lowerMsg.includes('pathway') || lowerMsg.includes('mechanism')) intent = 'mechanism';
    else if (lowerMsg.includes('expression') || lowerMsg.includes('tissue')) intent = 'expression';
    else if (lowerMsg.includes('perturbation') || lowerMsg.includes('knockout')) intent = 'perturbation';
    else if (lowerMsg.includes('compare') || lowerMsg.includes('vs')) intent = 'comparison';
    
    // Detect therapeutic area
    let ta = conversationState.lastTherapeuticArea;
    if (lowerMsg.includes('heart') || lowerMsg.includes('cardio')) ta = 'cardiovascular';
    else if (lowerMsg.includes('kidney') || lowerMsg.includes('renal')) ta = 'renal';
    
    setConversationState({
      lastGenes: uniqueGenes.length > 0 ? uniqueGenes : conversationState.lastGenes,
      lastIntent: intent,
      lastTopic: userMessage,
      lastTherapeuticArea: ta
    });
  }, [extractGenesFromText, conversationState.lastGenes, conversationState.lastTherapeuticArea]);

  // Handle gene click
  const handleGeneClick = useCallback((gene: string) => {
    if (onNavigateToGene) {
      onNavigateToGene(gene);
    } else {
      // Default: ask Genie about the gene
      handleSendMessage(`Tell me about ${gene}`);
    }
  }, [onNavigateToGene]);

  const handleSendMessage = useCallback(async (messageText?: string) => {
    let text = messageText || inputValue.trim();
    if (!text || isLoading) return;

    setError(null);
    setInputValue('');

    // Resolve context references (pronouns, "tell me more", etc.)
    text = resolveContextReferences(text);

    // Add user message
    const userMessage: ChatMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Build enhanced context with conversation state
      const enhancedContext: ChatContext = {
        ...context,
        current_gene: conversationState.lastGenes[0] || context?.current_gene,
        therapeutic_area: conversationState.lastTherapeuticArea || context?.therapeutic_area
      };

      const response: ChatResponse = await genieClient.chat({
        message: text,
        conversation_history: messages,
        context: enhancedContext
      });

      // Add assistant response
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.message,
        timestamp: response.timestamp
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      
      // Update conversation state
      updateConversationState(text, response);
      
      // Update suggestions
      if (response.suggestions.length > 0) {
        setSuggestions(response.suggestions);
      }

    } catch (err) {
      console.error('Genie chat error:', err);
      setError('Sorry, I encountered an error. Please try again.');
      
      // Add error message
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, messages, context, conversationState, resolveContextReferences, updateConversationState]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle autocomplete navigation
    if (showAutocomplete && autocompleteResults.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedAutocompleteIndex(prev => 
            prev < autocompleteResults.length - 1 ? prev + 1 : prev
          );
          return;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedAutocompleteIndex(prev => prev > 0 ? prev - 1 : -1);
          return;
        case 'Tab':
        case 'Enter':
          if (selectedAutocompleteIndex >= 0) {
            e.preventDefault();
            handleAutocompleteSelect(autocompleteResults[selectedAutocompleteIndex]);
            return;
          }
          break;
        case 'Escape':
          e.preventDefault();
          setShowAutocomplete(false);
          return;
      }
    }
    
    // Normal enter to send
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleAutocompleteSelect = (result: AutocompleteResult) => {
    // Replace the last word with the selected gene
    const words = inputValue.trim().split(/\s+/);
    words[words.length - 1] = result.symbol;
    setInputValue(words.join(' ') + ' ');
    setShowAutocomplete(false);
    setSelectedAutocompleteIndex(-1);
    inputRef.current?.focus();
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleSendMessage(suggestion);
  };

  // Export handlers
  const handleExportPdf = async () => {
    if (messages.length === 0) {
      setError('No messages to export');
      return;
    }
    
    setIsExporting(true);
    setError(null);
    setShowExportMenu(false);
    
    try {
      await exportChatAsPdf('genie-messages-container', undefined, true, messages);
    } catch (err) {
      console.error('PDF export error:', err);
      setError(`Failed to export PDF: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCsv = async () => {
    if (messages.length === 0) {
      setError('No messages to export');
      return;
    }
    
    setIsExporting(true);
    setError(null);
    setShowExportMenu(false);
    
    try {
      exportTablesAsCsv(messages);
    } catch (err) {
      console.error('CSV export error:', err);
      setError(`Failed to export CSV: ${err instanceof Error ? err.message : 'No tables found'}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportLastMessage = async (format: 'pdf' | 'csv') => {
    const lastAssistantIdx = [...messages].map((m, i) => ({ m, i })).reverse()
      .find(({ m }) => m.role === 'assistant' && !isWelcomeMessage(m.content));
    if (!lastAssistantIdx) {
      setError('No assistant message to export');
      return;
    }
    
    setIsExporting(true);
    setError(null);
    setShowExportMenu(false);
    
    try {
      const pair = getMessagePair(lastAssistantIdx.i);
      if (format === 'pdf') {
        await exportMessageAsPdf(pair, 'Genie Report');
      } else {
        exportMessageTablesAsCsv(pair);
      }
    } catch (err) {
      console.error('Export error:', err);
      setError(`Failed to export ${format.toUpperCase()}: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Find the user prompt + assistant response pair for a given assistant message index
  const getMessagePair = (assistantIndex: number): ChatMessage[] => {
    const pair: ChatMessage[] = [];
    if (assistantIndex > 0 && messages[assistantIndex - 1]?.role === 'user') {
      pair.push(messages[assistantIndex - 1]);
    }
    pair.push(messages[assistantIndex]);
    return pair;
  };

  // Handle export for a single message (from inline buttons)
  const handleExportSingleMessage = async (messageIndex: number, format: 'pdf' | 'html' | 'csv') => {
    setIsExporting(true);
    setError(null);
    
    try {
      const pair = getMessagePair(messageIndex);
      if (format === 'pdf') {
        await exportMessageAsPdf(pair, 'Genie Report');
      } else if (format === 'html') {
        exportMessageAsHtml(pair);
      } else {
        exportMessageTablesAsCsv(pair);
      }
    } catch (err) {
      console.error('Export error:', err);
      setError(`Failed to export ${format.toUpperCase()}: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Custom markdown components with gene link support
  const markdownComponents = useMemo(() => ({
    // Custom table styling
    table: ({ children, ...props }: React.HTMLAttributes<HTMLTableElement>) => (
      <div className="overflow-x-auto my-2">
        <table 
          className={`min-w-full text-xs border-collapse ${
            mode === 'light' ? 'border-gray-300' : 'border-slate-600'
          }`}
          {...props}
        >
          {children}
        </table>
      </div>
    ),
    thead: ({ children, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) => (
      <thead 
        className={`${mode === 'light' ? 'bg-gray-200' : 'bg-slate-700'}`}
        {...props}
      >
        {children}
      </thead>
    ),
    th: ({ children, ...props }: React.HTMLAttributes<HTMLTableCellElement>) => (
      <th 
        className={`px-2 py-1 text-left font-semibold border ${
          mode === 'light' ? 'border-gray-300' : 'border-slate-600'
        }`}
        {...props}
      >
        {children}
      </th>
    ),
    td: ({ children, ...props }: React.HTMLAttributes<HTMLTableCellElement>) => (
      <td 
        className={`px-2 py-1 border ${
          mode === 'light' ? 'border-gray-300' : 'border-slate-600'
        }`}
        {...props}
      >
        {children}
      </td>
    ),
    tr: ({ children, ...props }: React.HTMLAttributes<HTMLTableRowElement>) => (
      <tr 
        className={`${mode === 'light' ? 'even:bg-gray-50' : 'even:bg-slate-800'}`}
        {...props}
      >
        {children}
      </tr>
    ),
    // Headers
    h1: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <h1 className="text-lg font-bold mt-3 mb-2" {...props}>{children}</h1>
    ),
    h2: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <h2 className="text-base font-bold mt-3 mb-2" {...props}>{children}</h2>
    ),
    h3: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <h3 className="text-sm font-bold mt-2 mb-1" {...props}>{children}</h3>
    ),
    // Lists
    ul: ({ children, ...props }: React.HTMLAttributes<HTMLUListElement>) => (
      <ul className="list-disc list-inside my-1 space-y-0.5" {...props}>{children}</ul>
    ),
    ol: ({ children, ...props }: React.HTMLAttributes<HTMLOListElement>) => (
      <ol className="list-decimal list-inside my-1 space-y-0.5" {...props}>{children}</ol>
    ),
    li: ({ children, ...props }: React.HTMLAttributes<HTMLLIElement>) => (
      <li className="text-sm" {...props}>{children}</li>
    ),
    // Code
    code: ({ children, className, ...props }: React.HTMLAttributes<HTMLElement>) => {
      const isInline = !className;
      return isInline ? (
        <code 
          className={`px-1 py-0.5 rounded text-xs font-mono ${
            mode === 'light' ? 'bg-gray-200' : 'bg-slate-700'
          }`}
          {...props}
        >
          {children}
        </code>
      ) : (
        <code className={className} {...props}>{children}</code>
      );
    },
    // Paragraphs
    p: ({ children, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
      <p className="my-1 text-sm leading-relaxed" {...props}>{children}</p>
    ),
    // Strong text - check for gene symbols
    strong: ({ children, ...props }: React.HTMLAttributes<HTMLElement>) => {
      const text = String(children);
      const isGene = KNOWN_GENES.has(text.toUpperCase()) || 
        (text.match(/^[A-Z][A-Z0-9]{1,8}$/) && !COMMON_WORDS.has(text.toUpperCase()));
      
      if (isGene) {
        return (
          <button
            onClick={() => handleGeneClick(text.toUpperCase())}
            className={`font-bold cursor-pointer px-1 rounded transition-colors ${
              mode === 'light' 
                ? 'text-blue-600 hover:bg-blue-100' 
                : 'text-blue-400 hover:bg-blue-900/50'
            }`}
            title={`Click to learn more about ${text}`}
            {...props}
          >
            {children}
          </button>
        );
      }
      return <strong {...props}>{children}</strong>;
    },
    // Horizontal rule
    hr: ({ ...props }) => (
      <hr 
        className={`my-3 ${mode === 'light' ? 'border-gray-300' : 'border-slate-600'}`}
        {...props}
      />
    ),
    // Blockquote
    blockquote: ({ children, ...props }: React.HTMLAttributes<HTMLQuoteElement>) => (
      <blockquote 
        className={`border-l-4 pl-3 my-2 italic ${
          mode === 'light' ? 'border-blue-400 text-gray-600' : 'border-blue-500 text-slate-400'
        }`}
        {...props}
      >
        {children}
      </blockquote>
    ),
  }), [mode, handleGeneClick]);

  // Render message content with markdown
  const renderMessage = useCallback((content: string, isUser: boolean) => {
    if (isUser) {
      // Simple rendering for user messages
      return <span className="text-sm">{content}</span>;
    }
    
    return (
      <div className="markdown-content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={markdownComponents}
        >
          {content}
        </ReactMarkdown>
      </div>
    );
  }, [markdownComponents]);

  return (
    <>
      {/* Floating Genie Button */}
      {
        showGinie && (
           <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-[15px] right-6 z-50 w-14 h-14 rounded-full shadow-2xl transition-all duration-300 transform hover:scale-110 flex items-center justify-center ${
          isOpen 
            ? 'bg-gradient-to-r from-purple-600 to-indigo-600 rotate-0' 
            : 'bg-gradient-to-r from-blue-500 to-purple-600 animate-pulse'
        }`}
        aria-label={isOpen ? 'Close Genie' : 'Open Genie'}
        title="Genie AI Assistant"
      >
        <GenieLogo className="w-8 h-8 text-white" />
      </button>
      )
      }     

      {/* Collapsible Panel — narrow drawer or full viewport when maximized */}
      <div
        className={`transition-all duration-300 ease-in-out ${
          !isOpen
            ? 'fixed top-0 right-0 h-full z-40 w-0 overflow-hidden'
            : isMaximized
              ? 'fixed inset-0 z-[100] w-full h-full overflow-hidden'
              : 'fixed top-0 right-0 h-full z-40 w-[450px] overflow-hidden'
        }`}
        onTransitionEnd={(e) => {
          if (e.propertyName !== 'width' || e.target !== e.currentTarget) return;
          if (!isOpen) setHideScrollFab(false);
        }}
      >
        <div
          className={`h-full flex flex-col shadow-2xl ${
            isMaximized
              ? 'w-full border-0'
              : 'w-[450px] border-l'
          } ${
            mode === 'light'
              ? 'bg-white border-gray-200'
              : 'bg-slate-900 border-slate-700'
          }`}
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-4 border-b ${
            mode === 'light' ? 'border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50' : 'border-slate-700 bg-gradient-to-r from-slate-800 to-slate-900'
          }`}>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                <GenieLogo className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className={`font-bold text-lg ${mode === 'light' ? 'text-gray-900' : 'text-white'}`}>
                    Genie
                  </h2>
                  <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider rounded-full leading-none ${
                    mode === 'light' 
                      ? 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 ring-1 ring-inset ring-amber-600/20' 
                      : 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-300 ring-1 ring-inset ring-amber-400/30'
                  }`}>
                    <span className={`mr-1 w-1.5 h-1.5 rounded-full ${
                      mode === 'light' ? 'bg-amber-500' : 'bg-amber-400'
                    }`}></span>
                    Experimental
                  </span>
                </div>
                <p className={`text-xs ${mode === 'light' ? 'text-gray-500' : 'text-slate-400'}`}>
                  AI Assistant
                  {conversationState.lastGenes.length > 0 && (
                    <span className="ml-1">
                      · Discussing: {conversationState.lastGenes.slice(0, 2).join(', ')}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              {/* Export button with dropdown */}
              {messages.length > 1 && (
                <div className="relative">
                  <button
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    disabled={isExporting}
                    className={`p-2 rounded-lg transition-colors ${
                      mode === 'light' 
                        ? 'hover:bg-gray-100 text-gray-500' 
                        : 'hover:bg-slate-800 text-slate-400'
                    } ${isExporting ? 'opacity-50' : ''}`}
                    aria-label="Export"
                    title="Export conversation"
                  >
                    {isExporting ? (
                      <svg className="h-4 w-4 shrink-0 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    )}
                  </button>
                  
                  {/* Export dropdown menu */}
                  {showExportMenu && (
                    <div 
                      className={`absolute right-0 mt-2 w-56 rounded-lg shadow-lg border z-50 ${
                        mode === 'light' 
                          ? 'bg-white border-gray-200' 
                          : 'bg-slate-800 border-slate-600'
                      }`}
                    >
                      <div className={`px-3 py-2 border-b text-xs font-semibold ${
                        mode === 'light' ? 'border-gray-100 text-gray-500' : 'border-slate-700 text-slate-400'
                      }`}>
                        Export Options
                      </div>
                      
                      <div className="py-1">
                        <button
                          onClick={handleExportPdf}
                          className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-2 ${
                            mode === 'light' 
                              ? 'hover:bg-gray-50 text-gray-700' 
                              : 'hover:bg-slate-700 text-slate-200'
                          }`}
                        >
                          <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                          <span>Export Chat as PDF</span>
                        </button>
                        
                        <button
                          onClick={async () => {
                            setIsExporting(true);
                            setShowExportMenu(false);
                            setError(null);
                            try {
                              exportChatAsHtml(messages);
                            } catch (err) {
                              console.error('Export error:', err);
                              setError(`Export failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
                            } finally {
                              setIsExporting(false);
                            }
                          }}
                          className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-2 ${
                            mode === 'light' 
                              ? 'hover:bg-gray-50 text-gray-700' 
                              : 'hover:bg-slate-700 text-slate-200'
                          }`}
                        >
                          <svg className="w-4 h-4 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          <span>Download as HTML</span>
                        </button>
                        
                        <button
                          onClick={handleExportCsv}
                          className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-2 ${
                            mode === 'light' 
                              ? 'hover:bg-gray-50 text-gray-700' 
                              : 'hover:bg-slate-700 text-slate-200'
                          }`}
                        >
                          <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                          <span>Export Tables as CSV</span>
                        </button>
                      </div>
                      
                      <div className={`border-t py-1 ${
                        mode === 'light' ? 'border-gray-100' : 'border-slate-700'
                      }`}>
                        <div className={`px-3 py-1 text-xs ${
                          mode === 'light' ? 'text-gray-400' : 'text-slate-500'
                        }`}>
                          Last Response Only
                        </div>
                        
                        <button
                          onClick={() => handleExportLastMessage('pdf')}
                          className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-2 ${
                            mode === 'light' 
                              ? 'hover:bg-gray-50 text-gray-700' 
                              : 'hover:bg-slate-700 text-slate-200'
                          }`}
                        >
                          <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                          </svg>
                          <span>Save Report as PDF</span>
                        </button>
                        
                        <button
                          onClick={() => handleExportLastMessage('csv')}
                          className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-2 ${
                            mode === 'light' 
                              ? 'hover:bg-gray-50 text-gray-700' 
                              : 'hover:bg-slate-700 text-slate-200'
                          }`}
                        >
                          <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          <span>Download Data as CSV</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Clear conversation button */}
              {messages.length > 1 && (
                <button
                  onClick={() => {
                    setMessages([]);
                    setConversationState({
                      lastGenes: [],
                      lastIntent: null,
                      lastTopic: null,
                      lastTherapeuticArea: null
                    });
                  }}
                  className={`p-2 rounded-lg transition-colors ${
                    mode === 'light' 
                      ? 'hover:bg-gray-100 text-gray-500' 
                      : 'hover:bg-slate-800 text-slate-400'
                  }`}
                  aria-label="Clear conversation"
                  title="Clear conversation"
                >
                  <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsMaximized((m) => !m)}
                className={`p-2 rounded-lg transition-colors ${
                  mode === 'light'
                    ? 'hover:bg-gray-100 text-gray-500'
                    : 'hover:bg-slate-800 text-slate-400'
                }`}
                aria-label={isMaximized ? 'Exit fullscreen' : 'Maximize chat'}
                title={isMaximized ? 'Exit fullscreen' : 'Maximize chat'}
              >
                {isMaximized ? (
                  <ArrowsPointingInIcon className="h-4 w-4 shrink-0" />
                ) : (
                  <ArrowsPointingOutIcon className="h-4 w-4 shrink-0" />
                )}
              </button>
              <button
                onClick={() => { setIsOpen(false); setShowGenie(true); setIsMaximized(false); }} // Close panel and show floating button
                className={`p-2 rounded-lg transition-colors ${
                  mode === 'light' 
                    ? 'hover:bg-gray-100 text-gray-500' 
                    : 'hover:bg-slate-800 text-slate-400'
                }`}
                aria-label="Close panel"
              >
                <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div 
            id="genie-messages-container"
            className={`flex-1 overflow-y-auto p-4 space-y-4 ${
              mode === 'light' ? 'custom-scrollbar' : 'darkcustom-scrollbar'
            }`}
          >
            {messages.map((message, index) => {
              const isWelcome = message.role === 'assistant' && isWelcomeMessage(message.content);
              const showExportOptions = message.role === 'assistant' && !isWelcome;
              
              return (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-welcome-message={isWelcome ? 'true' : undefined}
                >
                  <div className="flex flex-col max-w-[90%]">
                    <div
                      className={`rounded-2xl px-4 py-3 ${
                        message.role === 'user'
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-br-md'
                          : mode === 'light'
                            ? 'bg-gray-100 text-gray-800 rounded-bl-md'
                            : 'bg-slate-800 text-slate-100 rounded-bl-md'
                      }`}
                    >
                      {renderMessage(message.content, message.role === 'user')}
                    </div>
                    
                    {/* Export buttons for assistant messages (except welcome) */}
                    {showExportOptions && (
                      <div className={`flex items-center gap-2 mt-1 ml-1 ${
                        mode === 'light' ? 'text-gray-400' : 'text-slate-500'
                      }`}>
                        <button
                          onClick={() => handleExportSingleMessage(index, 'pdf')}
                          className={`text-xs flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                            mode === 'light' 
                              ? 'hover:bg-gray-100 hover:text-gray-600' 
                              : 'hover:bg-slate-800 hover:text-slate-300'
                          }`}
                          title="Export this message as PDF"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                          PDF
                        </button>
                        <button
                          onClick={() => handleExportSingleMessage(index, 'html')}
                          className={`text-xs flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                            mode === 'light' 
                              ? 'hover:bg-gray-100 hover:text-gray-600' 
                              : 'hover:bg-slate-800 hover:text-slate-300'
                          }`}
                          title="Export this message as HTML"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          HTML
                        </button>
                        <button
                          onClick={() => handleExportSingleMessage(index, 'csv')}
                          className={`text-xs flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                            mode === 'light' 
                              ? 'hover:bg-gray-100 hover:text-gray-600' 
                              : 'hover:bg-slate-800 hover:text-slate-300'
                          }`}
                          title="Export tables from this message as CSV"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                          CSV
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className={`rounded-2xl px-4 py-3 rounded-bl-md ${
                  mode === 'light' ? 'bg-gray-100' : 'bg-slate-800'
                }`}>
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 rounded-full bg-purple-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className={`text-xs ${mode === 'light' ? 'text-gray-500' : 'text-slate-400'}`}>
                      Thinking...
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Suggestions */}
          {messages.length <= 2 && (
            <div className={`px-4 py-2 border-t ${
              mode === 'light' ? 'border-gray-100 bg-gray-50' : 'border-slate-800 bg-slate-900/50'
            }`}>
              <p className={`text-xs mb-2 ${mode === 'light' ? 'text-gray-500' : 'text-slate-500'}`}>
                Try asking:
              </p>
              <div className="flex flex-wrap gap-2">
                {suggestions.slice(0, 3).map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    disabled={isLoading}
                    className={`text-xs px-3 py-1.5 rounded-full transition-all ${
                      mode === 'light'
                        ? 'bg-white border border-gray-200 text-gray-700 hover:border-blue-300 hover:text-blue-600'
                        : 'bg-slate-800 border border-slate-700 text-slate-300 hover:border-blue-500 hover:text-blue-400'
                    } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Context indicator */}
          {conversationState.lastGenes.length > 0 && messages.length > 2 && (
            <div className={`px-4 py-2 border-t ${
              mode === 'light' ? 'border-gray-100 bg-blue-50' : 'border-slate-800 bg-blue-900/20'
            }`}>
              <p className={`text-xs ${mode === 'light' ? 'text-blue-600' : 'text-blue-400'}`}>
                💡 You can say "tell me more" or "same for [gene]" to continue the conversation
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="px-4 py-2">
              <div className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 p-2 rounded-lg">
                {error}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className={`p-4 border-t ${
            mode === 'light' ? 'border-gray-200 bg-white' : 'border-slate-700 bg-slate-900'
          }`}>
            {/* Autocomplete Dropdown */}
            {showAutocomplete && autocompleteResults.length > 0 && (
              <div 
                ref={autocompleteRef}
                className={`mb-2 rounded-lg overflow-hidden shadow-lg border ${
                  mode === 'light' 
                    ? 'bg-white border-gray-200' 
                    : 'bg-slate-800 border-slate-600'
                }`}
              >
                <div className={`px-3 py-1.5 text-xs font-medium ${
                  mode === 'light' ? 'bg-gray-50 text-gray-500' : 'bg-slate-700 text-slate-400'
                }`}>
                  🧬 Gene suggestions
                </div>
                {autocompleteResults.map((result, index) => (
                  <button
                    key={result.symbol}
                    onClick={() => handleAutocompleteSelect(result)}
                    className={`w-full px-3 py-2 text-left flex items-center justify-between transition-colors ${
                      index === selectedAutocompleteIndex
                        ? mode === 'light'
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-blue-900/50 text-blue-300'
                        : mode === 'light'
                          ? 'hover:bg-gray-50 text-gray-800'
                          : 'hover:bg-slate-700 text-slate-200'
                    }`}
                  >
                    <div>
                      <span className="font-semibold text-sm">{result.symbol}</span>
                      {result.name && (
                        <span className={`ml-2 text-xs ${
                          mode === 'light' ? 'text-gray-500' : 'text-slate-400'
                        }`}>
                          {result.name.length > 40 ? result.name.substring(0, 40) + '...' : result.name}
                        </span>
                      )}
                    </div>
                    <span className={`text-xs ${
                      mode === 'light' ? 'text-gray-400' : 'text-slate-500'
                    }`}>
                      Tab to select
                    </span>
                  </button>
                ))}
              </div>
            )}
            
            <div className={`flex items-center space-x-2 rounded-xl p-2 ${
              mode === 'light' ? 'bg-gray-100' : 'bg-slate-800'
            }`}>
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  setShowGenie(false); // Hide Genie when text input is focused
                  // Re-check autocomplete when input is focused
                  if (autocompleteResults.length > 0) {
                    setShowAutocomplete(true);
                  }
                }}
                placeholder={conversationState.lastGenes.length > 0 
                  ? `Ask about ${conversationState.lastGenes[0]} or another gene...`
                  : "Ask Genie anything..."}
                disabled={isLoading}
                autoComplete="off"
                className={`flex-1 bg-transparent border-none outline-none text-sm px-2 ${
                  mode === 'light' 
                    ? 'text-gray-800 placeholder-gray-400' 
                    : 'text-white placeholder-slate-500'
                }`}
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={!inputValue.trim() || isLoading}
                className={`p-2 rounded-lg transition-all ${
                  inputValue.trim() && !isLoading
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:shadow-lg transform hover:scale-105'
                    : mode === 'light'
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                }`}
                aria-label="Send message"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
            <p className={`text-xs mt-2 text-center ${
              mode === 'light' ? 'text-gray-400' : 'text-slate-600'
            }`}>
              Powered by Azure AI · Click genes to explore
            </p>
          </div>
        </div>
      </div>

      {/* Backdrop for mobile (hidden when maximized — panel already fills the screen) */}
      {isOpen && !isMaximized && (
        <div 
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default Genie;
